<template>
    <PageLayoutForms>
        <template #header>
            <h1 class="pdp-mandate-onboarding-company__title typographies-heading-h1">
                {{ $t.__('pdp.mandate.Onboarding.title') }}
            </h1>
        </template>
        <template #content>
            <InformationBanner
                class="pdp-mandate-onboarding-company__information-banner"
                :title="$t.__('pdp.mandate.OnboardingCompany.InformationBanner.title')"
                sourceIllustration="/ressources/logged/img/illustrations/pdp/user-identity.svg"
            >
                <template #description>
                    <div
                        class="pdp-mandate-onboarding-company__information typographies-paragraph-medium-regular"
                        v-html="informationBannerDescription"
                    />
                </template>
            </InformationBanner>
            <div class="pdp-mandate-onboarding-company__form-item">
                <CompassFormItem
                    :label="$t.__('pdp.mandate.OnboardingCompany.formItem.company')"
                    size="large"
                >
                    <CompassAutoComplete
                        v-model="form.company"
                        size="large"
                        :multiple="true"
                        :multipleLimit="1"
                        :results="searchResults"
                        :noResultsLabel="$t.__('common.labels.no_match_found')"
                        :placeholder="
                            $t.__('pdp.mandate.OnboardingCompany.formItem.company.placeholder')
                        "
                        menuClass="pdp-mandate-onboarding-company__company-search"
                        :loading="loadingCompanySearch"
                        @change="handleCompanySelect"
                        @update-search="handleCompanySearch"
                    />
                </CompassFormItem>
                <div
                    class="pdp-mandate-onboarding-company__help typographies-paragraph-small-semibold"
                >
                    <span>
                        {{ $t.__('pdp.mandate.OnboardingCompany.formItem.company.help') }}
                    </span>
                    <CompassPlainButton
                        :label="$t.__('pdp.mandate.OnboardingCompany.support_button')"
                        size="small"
                        @click="openIntercom"
                    />
                </div>
                <template v-if="showCompanyInformation">
                    <div class="pdp-mandate-onboarding-company__company-information">
                        <CompassDescriptionList size="small">
                            <CompassDescriptionListItem
                                v-for="(item, index) in Object.values(form.company[0])"
                                :key="index"
                                :label="item.label"
                            >
                                {{ item.value }}
                            </CompassDescriptionListItem>
                        </CompassDescriptionList>
                    </div>
                    <div class="pdp-mandate-onboarding-company__company-information-help">
                        <img
                            src="/ressources/logged/img/illustrations/pdp/information.svg"
                            alt="Information"
                            height="20"
                            width="20"
                        />
                        <span class="typographies-paragraph-medium-regular">
                            {{
                                $t.__(
                                    'pdp.mandate.OnboardingCompany.formItem.company_information.help',
                                )
                            }}
                        </span>
                    </div>
                </template>
            </div>
            <div class="pdp-mandate-onboarding-company__form-item">
                <CompassFormItem
                    :label="$t.__('pdp.mandate.OnboardingCompany.formItem.legal_representative')"
                    size="large"
                    :info="
                        $t.__('pdp.mandate.OnboardingCompany.formItem.legal_representative.info')
                    "
                >
                    <CompassSelect
                        v-model="form.legalRepresentative"
                        size="large"
                        menuClass="pdp-mandate-onboarding-company__company-legal-representative"
                    >
                        <CompassDropdownSelectOption
                            v-for="item in legalRepresentatives"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        />
                    </CompassSelect>
                </CompassFormItem>
                <div
                    class="pdp-mandate-onboarding-company__help typographies-paragraph-small-semibold"
                >
                    <span>
                        {{
                            $t.__(
                                'pdp.mandate.OnboardingCompany.formItem.legal_representative.help',
                            )
                        }}
                    </span>
                    <CompassPlainButton
                        :label="$t.__('pdp.mandate.OnboardingCompany.support_button')"
                        size="small"
                        @click="openIntercom"
                    />
                </div>
            </div>
        </template>
        <template #footer>
            <FooterFullPage :primaryAction="primaryAction" :secondaryActions="secondaryActions" />
        </template>
    </PageLayoutForms>
</template>
<script>
import InformationBanner from 'vcomponents/common/InformationBanner/InformationBanner.vue';
import FooterFullPage from 'vcomponents/common/FooterFullPage/FooterFullPage.vue';
import CompaniesService from 'api/companies';
import PageLayoutForms from 'vcomponents/common/layout/pages/PageLayoutForms.vue';

export default {
    components: {
        InformationBanner,
        FooterFullPage,
        PageLayoutForms,
    },
    props: {
        selectedMandateType: {
            type: String,
            required: true,
            validator: (value) => ['issue', 'receipt'].includes(value),
        },
    },
    data() {
        return {
            primaryAction: {
                label:
                    this.selectedMandateType === 'receipt'
                        ? `${this.$t.__('common.actions.continue')} (1/2)`
                        : this.$t.__('common.actions.continue'),
                action: () => {
                    this.$emit('companySelected', this.form);
                },
            },
            secondaryActions: [
                {
                    label: this.$t.__('common.actions.cancel'),
                    action: () => {
                        this.$router.push({ name: 'dashboard' });
                    },
                },
            ],
            informationBannerDescription: this.$t.__(
                'pdp.mandate.OnboardingCompany.InformationBanner.description',
                {
                    paragraphStart: '<p>',
                    paragraphEnd: '</p>',
                },
            ),
            searchResults: [],
            legalRepresentatives: [
                {
                    value: 1,
                    label: 'John Doe',
                },
                {
                    value: 2,
                    label: 'Jane Smith',
                },
            ],
            form: {
                company: [
                    {
                        name: {
                            label: this.$t.__('common.labels.company_name'),
                            value: 'Nestlé Purina Petcare France',
                        },
                        type: {
                            label: this.$t.__('common.labels.company_type'),
                            value: 'SARL',
                        },
                        capital: {
                            label: this.$t.__('common.labels.company_capital'),
                            value: '21 091 872,00 €',
                        },
                        vat: {
                            label: this.$t.__('common.labels.company_vat'),
                            value: 'FR87302079462',
                        },
                        siret: {
                            label: this.$t.__('common.labels.siret'),
                            value: '302 079 462 00263',
                        },
                        siren: {
                            label: this.$t.__('common.labels.siren'),
                            value: '302 079 462',
                        },
                        apeNafCode: {
                            label: this.$t.__('common.labels.ape_naf_code'),
                            value: '10.92Z',
                        },
                        rcsImmatriculation: {
                            label: this.$t.__('common.labels.rcs_immatriculation'),
                            value: '302 079 462 R.C.S. Nanterre',
                        },
                    },
                ],
                legalRepresentative: '',
            },
            errors: {
                phone: false,
                email: false,
            },
            loadingCompanySearch: false,
        };
    },
    computed: {
        showCompanyInformation() {
            return this.form.company.length > 0;
        },
    },
    methods: {
        openIntercom() {
            if (window.Intercom) {
                window.Intercom('show');
            }
        },
        handleCompanySearch(value) {
            this.loadingCompanySearch = true;
            if (!value) {
                this.searchResults = [];
                this.loadingCompanySearch = false;
                return;
            }
            CompaniesService.getCompaniesBySearchScan({
                q: value,
                countryCode: 'FR',
            })
                .then((response) => {
                    // this.searchResults = response.data.map((item) => ({
                    //     label: item.label,
                    //     value: item.value,
                    //     siret: item.siret,
                    // }));
                    console.log('Companies fetched:', response.data);
                })
                .catch((error) => {
                    console.error('Error fetching companies:', error);
                })
                .finally(() => {
                    this.loadingCompanySearch = false;
                });
        },
        handleCompanySelect(value) {
            console.log('Selected company:', value);
        },
    },
};
</script>
<style lang="scss">
body:has(.pdp-mandate-onboarding-company) {
    #sellsy_app {
        background-color: var(--colors-common-background-default);
    }
}
#compass-dropdown-container {
    .pdp-mandate-onboarding-company__company-search {
        width: auto;
    }
    .pdp-mandate-onboarding-company__company-legal-representative {
        z-index: 101;
    }
}
</style>
<style lang="scss" scoped>
.pdp-mandate-onboarding-company {
    &__information-banner {
        margin-bottom: var(--spacing-4);
    }

    &__information {
        color: var(--colors-common-text-brand-secondary-blue);

        &::v-deep {
            p {
                margin: 0;

                & + ul {
                    list-style: revert;
                    margin: var(--spacing-3) 0 0;
                    padding: revert;
                }
            }
        }
    }

    &__form-item {
        margin: 0 0 var(--spacing-4);
    }

    &__help {
        color: var(--colors-common-text-subtle-default);
        margin: var(--spacing-1) 0 0;
    }

    &__company-information {
        background: var(--colors-common-background-subtle);
        border-radius: var(--spacing-1);
        color: var(--colors-common-text-standard-default);
        margin: var(--spacing-2) 0 var(--spacing-1);
        padding: var(--spacing-2);

        &-help {
            display: flex;
            gap: var(--spacing-1);
        }
    }
}
</style>
